import type { SupabaseClient } from '@supabase/supabase-js'
import type { VerifyOtp, VerifyOtpResult } from '@/modules/auth/use-cases'
import {
  InvalidOtpError,
  NetworkError,
  OtpExpiredError,
  PhoneLinkedToDifferentAccountError,
  RateLimitedError,
} from '@/shared/domain'
import type { HttpPost } from '@/shared/infra/http'
import { HttpError } from '@/shared/infra/http'
import type { OtpFlowMemory } from '../otp-flow-memory'

type VerifyEmailSigninOtpBody = { email: string }

type VerifyPhoneSigninOtpBody = { phone: string; code: string }

type VerifyPhoneSigninOtpResult = { email: string; tokenHash: string }

type VerifyOtpCredentials = Parameters<SupabaseClient['auth']['verifyOtp']>[0]

export class SupabaseVerifyOtpRepository {
  constructor(
    private readonly client: SupabaseClient,
    private readonly anonHttpClient: HttpPost,
    private readonly authedHttpClient: HttpPost,
    private readonly flowMemory: OtpFlowMemory,
  ) {}

  execute: VerifyOtp = async ({ identifier, token, channel }) => {
    if (channel === 'phone') {
      const isPhoneLinkFlow = this.flowMemory.recall('phone', identifier) === 'link'
      try {
        if (!isPhoneLinkFlow) {
          return await this.verifyWithSupabase({ phone: identifier, token, type: 'sms' })
        }
        const { tokenHash } = await this.exchangePhoneCode(identifier, token)
        return await this.verifyWithSupabase({ token_hash: tokenHash, type: 'magiclink' })
      } finally {
        this.flowMemory.forget('phone', identifier)
      }
    }
    const email = identifier.toLowerCase().trim()
    const isLinkFlow = this.flowMemory.recall('email', email) === 'link'
    try {
      const result = await this.verifyWithSupabase({
        email: identifier,
        token,
        type: isLinkFlow ? 'magiclink' : 'email',
      })
      if (isLinkFlow) {
        await this.stampEmailVerified(email)
      }
      return result
    } finally {
      this.flowMemory.forget('email', email)
    }
  }

  private async verifyWithSupabase(
    credentials: VerifyOtpCredentials,
  ): Promise<VerifyOtpResult> {
    try {
      const { data, error } = await this.client.auth.verifyOtp(credentials)
      if (error) {
        if (error.code === 'otp_expired') {
          throw new OtpExpiredError()
        }
        if (error.code === 'otp_invalid' || error.code === 'invalid_otp') {
          throw new InvalidOtpError()
        }
        if (error.status === 429) {
          throw new RateLimitedError()
        }
        throw error
      }
      const accessToken = data.session?.access_token
      if (!accessToken) {
        throw new Error('verify-otp did not return an access token')
      }
      return { accessToken }
    } catch (err) {
      if (
        err instanceof InvalidOtpError ||
        err instanceof OtpExpiredError ||
        err instanceof RateLimitedError
      ) {
        throw err
      }
      if (err instanceof TypeError) {
        throw new NetworkError()
      }
      throw err
    }
  }

  private async exchangePhoneCode(
    phone: string,
    code: string,
  ): Promise<VerifyPhoneSigninOtpResult> {
    try {
      const result = await this.anonHttpClient.post<
        VerifyPhoneSigninOtpBody,
        VerifyPhoneSigninOtpResult | null
      >({
        url: '/users/verify-phone-signin-otp',
        body: { phone, code },
      })
      if (!result || typeof result.tokenHash !== 'string' || result.tokenHash === '') {
        throw new Error('verify-phone-signin-otp did not return a token hash')
      }
      return result
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.status === 409) {
          throw new PhoneLinkedToDifferentAccountError()
        }
        if (err.status === 400) {
          throw new InvalidOtpError()
        }
      }
      if (err instanceof TypeError) {
        throw new NetworkError()
      }
      throw err
    }
  }

  private async stampEmailVerified(email: string): Promise<void> {
    try {
      await this.authedHttpClient.post<VerifyEmailSigninOtpBody, void>({
        url: '/users/verify-email-signin-otp',
        body: { email },
      })
    } catch {
      return
    }
  }
}
